import { BaseDao, type SupabaseClient, type InvalidateFn } from '../base';
import type { PostgrestError } from '@supabase/supabase-js';
import type { EdprRequestRow } from '../../types/edpr';

export class EdprRequestDao extends BaseDao<EdprRequestRow> {
  protected readonly dependentResources = ['edpr_requests'];

  constructor(sb: SupabaseClient, invalidateFn?: InvalidateFn) {
    super(sb, 'edpr_requests', invalidateFn);
  }

  /** Fetch all non-deleted requests. */
  async getAll(): Promise<EdprRequestRow[]> {
    const { data, error } = await this.sb
      .from(this.table)
      .select('*')
      .eq('is_deleted', false)
      .order('year', { ascending: false })
      .order('request_date', { ascending: false });
    if (error) throw error;
    return data as EdprRequestRow[];
  }

  /** Fetch by request_number (request number). */
  async getByExternalId(externalId: string): Promise<EdprRequestRow | null> {
    const { data, error }: { data: EdprRequestRow | null; error: PostgrestError | null } = await this.sb
      .from(this.table)
      .select('*')
      .eq('request_number', externalId)
      .maybeSingle();
    if (error) throw error;
    return data;
  }

  /** Fetch all existing requests keyed by request_number — for diff computation. */
  async getAllByExternalId(): Promise<Map<string, EdprRequestRow>> {
    const rows = await this.getAll();
    return new Map(rows.map(r => [r.request_number, r]));
  }

  /** Upsert a single request by request_number. */
  async upsertByExternalId(
    row: Omit<Partial<EdprRequestRow>, 'id' | 'created_at' | 'updated_at'> & { request_number: string },
  ): Promise<EdprRequestRow> {
    const { data, error }: { data: EdprRequestRow | null; error: PostgrestError | null } = await this.sb
      .from(this.table)
      .upsert(row as Record<string, unknown>, { onConflict: 'request_number' })
      .select('*')
      .single();
    if (error) throw error;
    // `.single()` reports a missing row through `error`, so `data` is set here.
    return data as EdprRequestRow;
  }

  /** Batch upsert by request_number. */
  async upsertMany(
    rows: Array<Omit<Partial<EdprRequestRow>, 'id' | 'created_at' | 'updated_at'> & { request_number: string }>,
  ): Promise<EdprRequestRow[]> {
    if (rows.length === 0) return [];
    const { data, error } = await this.sb
      .from(this.table)
      .upsert(rows as Record<string, unknown>[], { onConflict: 'request_number' })
      .select('*');
    if (error) throw error;
    this.invalidateAll();
    return data as EdprRequestRow[];
  }
}
